/**
 * URL and client-side sort helpers for the Scores list screen.
 * courseId omitted/blank => org-wide scores (all courses).
 */

/** Default sort used by the scores table (client-side only). */
export const SCORES_LIST_DEFAULTS = {
  sortBy: 'participant',
  sortDir: 'asc',
};

/** Fixed sort keys exposed by the scores table column headers. */
export const SCORES_SORT_FIELDS = {
  participant: 'participant',
  courseName: 'courseName',
};

/**
 * Computes the next client-side sort when a column header is clicked.
 * Same column toggles asc/desc; a new column always starts asc.
 */
export function getNextScoresSort(columnKey, currentSortBy, currentSortDir) {
  const sortBy = columnKey || SCORES_LIST_DEFAULTS.sortBy;
  if (currentSortBy === sortBy) {
    return {
      sortBy,
      sortDir: currentSortDir === 'asc' ? 'desc' : 'asc',
    };
  }
  return { sortBy, sortDir: 'asc' };
}

/**
 * Stable cache key for a scores list query keyed by userId + courseId.
 */
export function buildScoresListCacheKey(userId, { courseId = '' } = {}) {
  const cid = (courseId || '').trim();
  return `scores:${userId}:${cid || 'all'}`;
}

/**
 * Builds the scores list URL. When courseId is blank, calls the org-wide endpoint
 * without a filter; otherwise scopes via query param (legacy path still supported).
 */
export function buildScoresListUrl(apiUrl, userId, { courseId = '' } = {}) {
  const base = `${apiUrl}/api/studentcourses/results/${userId}`;
  const cid = (courseId || '').trim();
  if (!cid) return base;
  const params = new URLSearchParams();
  params.set('courseId', cid);
  return `${base}?${params.toString()}`;
}

/** True when sortBy refers to a dynamic assessment column (test name). */
function isAssessmentSortKey(sortBy) {
  return (
    sortBy !== SCORES_SORT_FIELDS.participant
    && sortBy !== SCORES_SORT_FIELDS.courseName
  );
}

/** Resolves a row field value for client-side column sorting. */
function scoreSortValue(row, sortBy) {
  if (sortBy === SCORES_SORT_FIELDS.courseName) {
    return row.courseName ?? '';
  }

  if (sortBy === SCORES_SORT_FIELDS.participant) {
    return `${row.firstName || ''} ${row.lastName || ''}`.trim();
  }

  const assessment = row[sortBy];
  if (assessment && typeof assessment.percentage === 'number') {
    return assessment.percentage;
  }

  return null;
}

/** Compares two resolved sort values; missing assessment scores always sort last. */
function compareScoreValues(av, bv, sortBy, dir) {
  if (isAssessmentSortKey(sortBy)) {
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    return (av - bv) * dir;
  }

  return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' }) * dir;
}

/**
 * Sorts grouped score rows in memory — no API round-trip.
 * Dynamic assessment columns use the test name as sortBy and sort by latest percentage.
 */
export function sortScoresLocally(rows, sortBy, sortDir) {
  const list = Array.isArray(rows) ? [...rows] : [];
  const dir = sortDir === 'desc' ? -1 : 1;
  const field = sortBy || SCORES_LIST_DEFAULTS.sortBy;

  list.sort((a, b) => {
    const av = scoreSortValue(a, field);
    const bv = scoreSortValue(b, field);
    return compareScoreValues(av, bv, field, dir);
  });

  return list;
}
